import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Microphone } from "@phosphor-icons/react";
import { toast } from "sonner";
import MemoRecorder from "@/pages/agenda/MemoRecorder";
import { isNative } from "@/lib/native";

/**
 * Global microphone FAB — "Parla a Pronto" da qualunque pagina dashboard.
 *
 * iter 258: ridotto a cerchio discreto 52×52 (era pill 200×56 con label
 * sempre visibile) + scroll-aware: si nasconde quando l'utente scrolla
 * verso il basso e riappare quando scrolla verso l'alto, così non copre i
 * contenuti durante la lettura. L'utente lamentava che il FAB precedente
 * dava fastidio durante la navigazione.
 *
 * Stato pagine:
 *  - hide hard: Agenda, Drive, Sopralluogo, Assistente (hanno già un mic)
 *  - hide su scroll-down: tutte le altre (riappare scrollando up)
 */
export default function GlobalMicFab() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const collapseTimer = useRef(null);
  const lastY = useRef(0);
  const location = useLocation();

  // iter v29 r081 — Nascondi il GlobalMicFab nel cantiere immobile.
  // Lì c'è già PropertyVoiceFab (modifica vocale dei campi annuncio) e
  // due microfoni in basso a destra che si sovrappongono creano confusione.
  // Il GlobalMicFab serve per Agenda/Note/Sopralluogo globali, NON per
  // editare il singolo immobile (quello lo fa PropertyVoiceFab).
  const isPropertyDetail = /^\/dashboard\/property\/[^/]+/.test(location.pathname);

  const MIC_CLICKS_KEY = "pronto:mic-fab:clicks";
  const FIRST_TOUR_KEY = "pronto:mic-fab:first-tour-seen";

  const scheduleCollapse = () => {
    if (collapseTimer.current) clearTimeout(collapseTimer.current);
    collapseTimer.current = setTimeout(() => setCollapsed(true), 10000);
  };
  const expandNow = () => {
    setCollapsed(false);
    scheduleCollapse();
  };

  // Schedule iniziale: pill estesa per 10s, poi cerchio.
  useEffect(() => {
    scheduleCollapse();
    return () => {
      if (collapseTimer.current) clearTimeout(collapseTimer.current);
    };
  }, []);

  // Scroll-aware: nasconde su scroll-down, riappare su scroll-up.
  // Threshold piccolo (12px) per evitare flicker.
  // iter v29 r045 — User: «la scritta Parla con Pronto sul pulsante in
  // basso a destra dopo un po' falla sparire». Comportamento: la pill
  // parte estesa per 10s, poi si COMPRIME in cerchio e RESTA così
  // (niente più re-expand su scroll-up). L'utente la riapre toccandola.
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;
      const dy = y - lastY.current;
      if (Math.abs(dy) < 12) return;
      if (y < 80) {
        setHidden(false);
      } else if (dy > 0) {
        setHidden(true);
      } else {
        // scroll-up → solo show (niente re-expand)
        setHidden(false);
      }
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter v29 r045 — User: «la scritta Parla con Pronto sul pulsante in
  // basso a destra dopo un po' falla sparire». Disabilitato il tour
  // pulse+tooltip iniziale: la pill si comprime dopo 10s e basta, senza
  // più bubble pop-up.
  useEffect(() => {
    // no-op: tour iniziale disabilitato
  }, []);

  const handleOpen = () => {
    setOpen(true);
    setPulsing(false);
    setShowTooltip(false);
    try {
      const n = parseInt(localStorage.getItem(MIC_CLICKS_KEY) || "0", 10);
      localStorage.setItem(MIC_CLICKS_KEY, String(n + 1));
      localStorage.setItem(FIRST_TOUR_KEY, "1");
    } catch { /* ignore */ }
  };

  // iter v28 r463 — Custom event "pronto:open-voice-fab" usato dal
  // HomeVoiceBanner per aprire il FAB dal banner home. Mantiene UI e
  // logica click counter unificate (1 punto di entry).
  useEffect(() => {
    const onOpen = () => handleOpen();
    window.addEventListener("pronto:open-voice-fab", onOpen);
    return () => window.removeEventListener("pronto:open-voice-fab", onOpen);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter v28 r480 — Early returns DOPO tutti gli hook (regola React).
  // Build production falliva per "React Hook called conditionally".
  const path = location.pathname || "";
  // iter 275: nascondi se non siamo nella dashboard (es. /login, /landing pubblica)
  if (!path.startsWith("/dashboard")) return null;
  // iter v29 r223 — User: «su iPhone togli il pulsante galleggiante,
  // tanto c'è già il bottone centrale (Assistente) nella bottom tab bar».
  // Su iOS/Android nativo nascondiamo del tutto il FAB. Sul web resta.
  if (isNative) return null;
  const hideHard = (
    path.startsWith("/dashboard/agenda") ||
    path.startsWith("/dashboard/drive") ||
    path.startsWith("/dashboard/sopralluogo") ||
    path.startsWith("/dashboard/assistente")
  );
  if (hideHard) return null;
  // iter v29 r081 — Nascondi nel cantiere immobile (c'è PropertyVoiceFab)
  if (isPropertyDetail) return null;

  return (
    <>
      {/* iter v28 r463 — Tooltip "Ehi, prova a parlare con me" alla prima apertura */}
      {showTooltip && (
        <div
          className={`fixed bottom-20 right-4 lg:bottom-24 lg:right-6 z-30 max-w-[260px] bg-[#1C1C1A] text-white rounded-2xl px-3.5 py-2.5 shadow-xl transition-opacity duration-500 ${
            hidden ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
          data-testid="global-mic-fab-tooltip"
        >
          <div className="text-[12.5px] font-semibold leading-snug">
            Ehi, sono Pronto. <span className="text-[#C8B886]">Parlami</span> — ti detto un promemoria, lo metto in agenda io.
          </div>
          <div className="text-[10.5px] text-white/60 mt-0.5">Tap sul microfono qui sotto</div>
          {/* arrow pointer */}
          <div className="absolute -bottom-2 right-7 w-4 h-4 bg-[#1C1C1A] rotate-45" />
        </div>
      )}

      <button
        onClick={handleOpen}
        onMouseEnter={expandNow}
        onMouseLeave={scheduleCollapse}
        style={{
          // iter v29 r072 — User M1437: FAB esce OVALE su mobile.
          // Causa: in collapsed state il <button> mobile applica padding
          // di default dal browser e il contenuto interno (cerchio 36×36
          // + testo whitespace-nowrap) stira la larghezza di qualche pixel.
          // Forziamo dimensioni esatte con inline style + box-sizing.
          // iter v29 r099 — User M1500: «centrami il pulsante del microfono
          // sul cerchio nero». Quando collapsed, il testo "Parla con Pronto"
          // restava nel DOM con max-w-0 + -mr-1 spostando il cerchio verde
          // di qualche pixel in basso/destra. Soluzione: non rendere il
          // testo quando collapsed così il cerchio verde è UNICO figlio
          // del flex e justify-center+items-center lo centra perfettamente.
          // iter v29 r168 — Safe area iPhone home indicator: aggiungiamo
          // env(safe-area-inset-bottom) al bottom così su iPhone X+
          // il FAB non viene coperto dalla barra-home.
          width: collapsed ? 48 : undefined,
          height: 48,
          padding: collapsed ? 0 : "0 12px",
          boxSizing: "border-box",
          borderRadius: 9999,
          bottom: "calc(20px + env(safe-area-inset-bottom))",
        }}
        className={`fixed right-4 lg:right-6 z-30 inline-flex items-center justify-center transition-all duration-500 active:scale-95 bg-[#1C1C1A] hover:bg-[#2A2A28] text-white ring-1 ring-white/15 shadow-[0_6px_24px_-4px_rgba(0,0,0,0.45)] ${
          collapsed
            ? "gap-0 overflow-hidden"
            : "gap-2"
        } ${
          pulsing ? "ring-4 ring-[#7C8B6E]/60 animate-pulse" : ""
        } ${
          hidden
            ? "translate-y-24 opacity-0 pointer-events-none"
            : "translate-y-0 opacity-100"
        }`}
        aria-label="Assistente · chiedi qualunque cosa a Pronto"
        title="Assistente · agenda vocale, sopralluoghi, note rapide"
        data-testid="global-mic-fab"
      >
        {!collapsed && (
          <span className="text-[12px] font-semibold leading-none whitespace-nowrap opacity-100 max-w-[180px]">
            Assistente
          </span>
        )}
        <span
          className="rounded-full bg-[#7C8B6E] inline-flex items-center justify-center flex-shrink-0"
          style={{ width: 32, height: 32, borderRadius: 9999 }}
        >
          <Microphone size={16} weight="fill" />
        </span>
      </button>
      <MemoRecorder
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => {
          toast.success("Memo salvato in Agenda", {
            duration: 3000,
            action: {
              label: "Apri agenda",
              onClick: () => { window.location.href = "/dashboard/agenda"; },
            },
          });
        }}
      />
    </>
  );
}
